import { useMutation } from '@apollo/client';
import { LoadingButton } from '@mui/lab';
import { Button, Grid, IconButton, Typography } from '@mui/material';
import TextField from 'components/Inputs/TextField';
import Section from 'components/Section';
import { useUser } from 'contexts/userContext';
import {
  DELETE_USER_GOALS_MUTATION,
  DeleteUserGoalsResponse,
  DeleteUserGoalsVariables,
  SAVE_START_YR_USER_GOALS,
  SUBMIT_START_YR_USERS_GOALS,
  SaveStartYrUserGoalsResponse,
  SaveStartYrUserGoalsVariables,
  SubmitStartYrUserGoalsReponse,
  SubmitStartYrUserGoalsVariables,
} from 'graphql/mutation/userGoals';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { User, UserGoal, UserStatusEnum } from 'types';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { numberRegex } from 'utils/regexes';
import { ConfirmationPopup } from 'components/PopUp';
import { getSelectedObjectiveGoalsYear } from 'utils/common';

type RowType = {
  _id: string;
  kra: string;
  kpi: string;
  weightage: number | string;
  target: string;
  user?: User;
};

const emptyRow = {
  _id: '',
  kra: '',
  kpi: '',
  weightage: '',
  target: '',
};

const emptyRows = [emptyRow, emptyRow, emptyRow, emptyRow, emptyRow];

const StartYearEvaluationForm: React.FC<{ initialRows?: UserGoal[]; employee: User }> = ({
  initialRows = emptyRows,
  employee,
}) => {
  const [rows, setRows] = useState<RowType[]>(
    initialRows.map((r, idx: number) => ({
      ...r,
      _id: !!r._id ? r._id : `new_${idx}`,
    }))
  );

  const [showDeletePopUp, toggleDeletePopUp] = useState({
    show: false,
    id: '',
  });

  const { user: loggedInUser } = useUser();

  const [saveStartYrUserGoals, { loading: savingUserGoals }] = useMutation<
    SaveStartYrUserGoalsResponse,
    SaveStartYrUserGoalsVariables
  >(SAVE_START_YR_USER_GOALS);

  const [submitStartYrUserGoals, { loading: submittingUserGoals }] = useMutation<
    SubmitStartYrUserGoalsReponse,
    SubmitStartYrUserGoalsVariables
  >(SUBMIT_START_YR_USERS_GOALS);

  const [deleteUserGoals, { loading: deletingUserGoals }] = useMutation<
    DeleteUserGoalsResponse,
    DeleteUserGoalsVariables
  >(DELETE_USER_GOALS_MUTATION);

  const navigate = useNavigate();

  const isManager = loggedInUser?._id === employee.reportingManager?._id;
  const isThemself = loggedInUser?._id === employee._id;
  const isUserSaved =
    employee.annualUserInfo?.startYearStatus === UserStatusEnum.DRAFT ||
    employee.annualUserInfo?.startYearStatus === UserStatusEnum['NOT STARTED'];
  const isAdmin = loggedInUser?.role.name === 'ADMIN';

  const currentYear = new Date().getFullYear();
  const selectedYear = getSelectedObjectiveGoalsYear();
  const isRunningYear = selectedYear === currentYear;

  const shouldEnableEdit = useMemo(() => {
    if (!isRunningYear) {
      return false;
    }
    if (isManager) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
        case UserStatusEnum['MANAGER APPROVED']:
          return false;
        case UserStatusEnum['PENDING FOR APPROVAL']:
          return true;
        default:
          return false;
      }
    } else if (isThemself) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return true;
        default:
          return false;
      }
    } else if (isAdmin) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return false;
        default:
          return true;
      }
    }
    return false;
  }, [isManager, isThemself, rows, isAdmin, isRunningYear]);

  const validateSubmitFields = () => {
    if (rows.length < 5) return false;
    let totalW = 0;
    for (let idx = 0; idx < rows.length; idx++) {
      const r = rows[idx];
      if (!!!r.kpi || !!!r.kra || !!!r.target || (r.weightage != 0 && !!!r.weightage)) {
        return false;
      }
      totalW += +r.weightage;
    }
    if (totalW < 100) return false;
    return true;
  };

  const shouldDisableSubmit = useMemo(() => {
    if (validateSubmitFields()) {
      if (isThemself) {
        switch (employee.annualUserInfo?.startYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return false;
          default:
            return true;
        }
      } else if (
        isManager &&
        employee.annualUserInfo?.startYearStatus === UserStatusEnum['PENDING FOR APPROVAL']
      ) {
        return false;
      } else if (isAdmin) {
        switch (employee.annualUserInfo?.startYearStatus) {
          case UserStatusEnum.DRAFT:
          case UserStatusEnum['NOT STARTED']:
            return true;
          default:
            return false;
        }
      }
    }
    return true;
  }, [employee, loggedInUser, rows, isThemself, isAdmin]);

  const handleSaveUserGoals = () => {
    const refinedRows = rows.map(currRow => {
      return {
        _id: currRow._id.split('_')[0] === 'new' ? undefined : currRow._id,
        kra: currRow.kra,
        kpi: currRow.kpi,
        user: employee._id,
        target: currRow.target,
        weightage: +currRow.weightage,
      } as SaveStartYrUserGoalsVariables['input']['0'];
    });

    saveStartYrUserGoals({
      variables: {
        // @ts-ignore
        input: refinedRows,
      },
      onCompleted: res => {
        toast.success('Saved Successfully');
        navigate(-1);
        setRows(res.updateStartYearUserGoals);
      },
    });
  };

  const handleSubmitGoals = () => {
    const refinedRows = rows.map(currRow => {
      return {
        _id: currRow._id.split('_')[0] === 'new' ? undefined : currRow._id,
        kra: currRow.kra,
        kpi: currRow.kpi,
        user: employee._id,
        target: currRow.target,
        weightage: +currRow.weightage,
      } as SubmitStartYrUserGoalsVariables['input']['0'];
    });

    submitStartYrUserGoals({
      variables: {
        // @ts-ignore
        input: refinedRows,
      },
      onCompleted: res => {
        setRows(res.submitStartYearUserGoals.userGoals);
        navigate(-1);
        toast.success('Employee goals have been submitted successfully');
      },
    });
  };

  const handleDeleteLineItem = () => {
    const id = showDeletePopUp.id;
    if (id.split('_')[0] === 'new') {
      setRows(prev => prev.filter(r => r._id !== id));
      toggleDeletePopUp({
        show: false,
        id: '',
      });
    } else
      deleteUserGoals({
        variables: {
          id,
          userId: employee._id,
        },
        onCompleted: () => {
          toggleDeletePopUp({
            show: false,
            id: '',
          });

          setRows(prev => prev.filter(r => r._id !== id));
        },
      });
  };

  const handleAddLineItem = () => {
    const newRow: RowType = {
      ...emptyRow,
      _id: `new_${Date.now().toString()}`,
    };

    const newRows = [...rows, newRow];
    setRows(newRows);

    setTimeout(() => {
      const lastRow = document.getElementById('last-element');
      if (lastRow) {
        lastRow.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const isDeletable = useMemo(() => {
    if (!isRunningYear) return false;
    if (isAdmin && !isUserSaved) {
      return true;
    }
    if (isThemself) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum.DRAFT:
        case UserStatusEnum['NOT STARTED']:
          return true;
        default:
          return false;
      }
    } else if (isManager) {
      switch (employee.annualUserInfo?.startYearStatus) {
        case UserStatusEnum['PENDING FOR APPROVAL']:
          return true;
        default:
          return false;
      }
    }
    return false;
  }, [employee.annualUserInfo?.startYearStatus, isAdmin, isRunningYear]);

  return (
    <form>
      <Grid container direction="column" rowGap={2.5} mb={14} position="relative">
        {rows.map((item, idx) => (
          <ObjectiveLineItem
            idx={idx}
            userGoal={item as UserGoal}
            setRows={setRows}
            rows={rows}
            key={idx}
            editable={shouldEnableEdit}
            handleDeleteLineItem={
              isDeletable
                ? () =>
                    toggleDeletePopUp({
                      id: item._id,
                      show: true,
                    })
                : undefined
            }
          />
        ))}
        <div id="last-element" style={{ marginBlock: 30 }} />
        <Grid
          container
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="sticky"
          bottom={0}
          right={0}
          left={0}
          py={3}
          px={3}
          bgcolor="white"
          zIndex={10}
          sx={{
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
          boxShadow={2}
          rowGap={2}
        >
          <Grid container item direction={'column'} xs={12} md={6} lg={8}>
            <Typography variant="caption" color="crimson" fontWeight={550} letterSpacing={1}>
              *All the fields KRA,KPI,Target & Weightage are required to be filled in order to
              Submit.
            </Typography>
            <Typography variant="caption" color="crimson" fontWeight={550} letterSpacing={1}>
              *The accumulated weightage must be 100%
            </Typography>
          </Grid>

          <Grid
            container
            item
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            columnGap={1.5}
            xs={12}
            md={6}
            lg={4}
          >
            {isRunningYear && (
              <>
                {((isManager &&
                  employee.annualUserInfo?.startYearStatus ===
                    UserStatusEnum['PENDING FOR APPROVAL']) ||
                  (isThemself && isUserSaved) ||
                  (isAdmin && !isUserSaved)) && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleAddLineItem}
                      disabled={rows.length == 8}
                    >
                      Add More
                    </Button>
                    <LoadingButton
                      variant="contained"
                      onClick={handleSaveUserGoals}
                      loading={savingUserGoals}
                    >
                      Save
                    </LoadingButton>
                  </>
                )}
                {isThemself && (
                  <LoadingButton
                    variant="contained"
                    disabled={shouldDisableSubmit}
                    loading={submittingUserGoals}
                    onClick={handleSubmitGoals}
                  >
                    Submit
                  </LoadingButton>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Grid>
      <ConfirmationPopup
        fullWidth
        key="delete-popup"
        onClose={() =>
          toggleDeletePopUp({
            show: false,
            id: '',
          })
        }
        open={showDeletePopUp.show}
        title="Do you want to delete?"
        onConfirmation={handleDeleteLineItem}
        loading={deletingUserGoals}
      />
    </form>
  );
};

const ObjectiveLineItem: React.FC<{
  idx: number;
  userGoal: UserGoal;
  rows: RowType[];
  editable: boolean;
  setRows: React.Dispatch<React.SetStateAction<RowType[]>>;
  handleDeleteLineItem?: () => void;
}> = ({ userGoal, setRows, rows, idx, editable, handleDeleteLineItem }) => {
  const handleChange = (fieldName: string, value: any) => {
    setRows(prev =>
      prev.map(curr => {
        if (curr._id === userGoal._id) {
          return { ...curr, [fieldName]: value };
        } else return curr;
      })
    );
  };

  const handleWeightChange = (value: any) => {
    const totalW = rows
      .filter(curr => curr._id !== userGoal._id)
      .reduce((acc, r) => acc + +r.weightage, 0);
    if (numberRegex.test(value) && totalW + +value <= 100) {
      handleChange('weightage', value);
    }
  };

  return (
    <Grid item>
      <Section
        title={'Serial No. ' + (idx + 1).toString()}
        collapsible
        action={
          !!handleDeleteLineItem && (
            <IconButton
              disabled={rows.length < 6}
              onClick={handleDeleteLineItem}
              color="inherit"
              size="small"
            >
              <DeleteForeverIcon />
            </IconButton>
          )
        }
      >
        <Grid container rowGap={1.5} columnSpacing={1.5} alignItems="center" px={0.5} py={0.5}>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              minRows={2}
              label="KRA"
              disabled={!editable}
              value={userGoal.kra ?? ''}
              onChange={e => handleChange('kra', e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              size="small"
              multiline
              disabled={!editable}
              minRows={2}
              label="KPI"
              value={userGoal.kpi ?? ''}
              onChange={e => handleChange('kpi', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Target"
              disabled={!editable}
              value={userGoal.target ?? ''}
              onChange={e => handleChange('target', e.target.value ?? '')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              size="small"
              label="Weightage"
              disabled={!editable}
              value={userGoal.weightage ?? ''}
              onChange={e => handleWeightChange(e.target.value)}
            />
          </Grid>
        </Grid>
      </Section>
    </Grid>
  );
};

export default StartYearEvaluationForm;
